import React, { useEffect, useState } from 'react';
import Module from 'root/main/components/module/Module';
import Cookies from 'js-cookie';
import LogoImage from 'root/main/components/regionalLogos/LogoImage';
import Debug from 'root/main/components/regionalLogos/Debug';
import type { Region, Footnote } from 'root/main/components/regionalLogos/types';

interface RegionalLogosProps {
    reactRegionalLogos: string | undefined;
}
interface RegionalLogosParsedData {
    defaultRegionCountryCode?: string;
    productType?: string;
    title?: string;
    titleFootnote?: Footnote;
    backgroundColor?: 'white' | 'grey' | 'black';
    regions?: Region[];
}

declare global {
    interface Window {
        registerCookieComplianceCallback: (params: {
            callback: () => void;
            category?: string;
            rejectCallback?: () => unknown;
            pendingCallback?: () => unknown;
            isCallOptanonWrapper?: boolean;
        }) => void;
    }
}

export default function RegionalLogos(props: RegionalLogosProps) {
    const MIN_LOGOS_COUNT = 3;
    const [userCountryCode, setUserCountryCode] = useState<string | undefined>('');
    const [userStateCode, setUserStateCode] = useState<string | undefined>('');
    const regionalLogosProps = (props.reactRegionalLogos &&
        JSON.parse(props.reactRegionalLogos)) as RegionalLogosParsedData | undefined;
    const [region, setRegion] = useState<Region>({
        country_code: userCountryCode,
        state_code: userStateCode,
        logos: [],
        title: regionalLogosProps?.title ?? '',
        title_footnote: {
            text: '',
            add_footnote: false,
            footnote: '',
        },
    });
    const { defaultRegionCountryCode, productType, backgroundColor, regions } =
        regionalLogosProps ?? {};
    const urlParams = new URLSearchParams(window.location.search);

    useEffect(() => {
        const geoCookie = Cookies.get('geoConfig');
        const viewFromParam = urlParams.get('view_from');
        if (geoCookie) {
            const countryCode = JSON.parse(geoCookie).country_code;
            const stateCode = JSON.parse(geoCookie).state_code;
            countryCode && setUserCountryCode(countryCode);
            window.registerCookieComplianceCallback({
                callback: () => stateCode && setUserStateCode(stateCode),
                category: 'targeting',
                rejectCallback: () => setUserStateCode(''),
            });
        }
        if (viewFromParam) {
            const [viewFromCountryCode, viewFromStateCode] =
                viewFromParam?.split('_') ?? [];
            viewFromCountryCode && setUserCountryCode(viewFromCountryCode);
            window.registerCookieComplianceCallback({
                callback: () => viewFromStateCode && setUserStateCode(viewFromStateCode),
                category: 'targeting',
                rejectCallback: () => setUserStateCode(''),
            });
        }
        if (regions) {
            setRegion(() => {
                return (
                    regions.find((region: Region) => {
                        if (
                            region.country_code === userCountryCode?.toLowerCase() &&
                            region.state_code === userStateCode?.toLowerCase() &&
                            region.logos.length >= MIN_LOGOS_COUNT
                        ) {
                            return region;
                        }
                    }) ??
                    regions.find((region: Region) => {
                        if (
                            region.country_code === userCountryCode?.toLowerCase() &&
                            !region.state_code &&
                            region.logos.length >= MIN_LOGOS_COUNT
                        ) {
                            return region;
                        }
                    }) ??
                    regions.find((region: Region) => {
                        if (
                            defaultRegionCountryCode &&
                            region.country_code === defaultRegionCountryCode &&
                            !region.state_code &&
                            region.logos.length >= MIN_LOGOS_COUNT
                        ) {
                            return region;
                        }
                    }) ??
                    // fallback
                    regions[0]
                );
            });
        }
    }, [userCountryCode, userStateCode]);

    const { logos } = region;
    const overrideTitle = Boolean(regionalLogosProps?.title);
    const title = overrideTitle ? regionalLogosProps?.title : region.title;
    const titleFootnote = overrideTitle
        ? regionalLogosProps?.titleFootnote
        : region.title_footnote;

    return (
        <>
            {logos.length > 0 && (
                <Module
                    moduleClassName="regional-logos logo-horizontal tw:py-6 tw:mob:py-12"
                    title={title}
                    titleSize="h3"
                    titleFootnote={titleFootnote}
                    backgroundColor={backgroundColor}
                >
                    {!!urlParams.get('debug_regional_logos') && (
                        <Debug region={region} reactProductType={productType} />
                    )}
                    <div
                        className="
                            tw:flex
                            tw:flex-wrap
                            tw:gap-1.5
                            tw:justify-center
                            tw:items-stretch
                        "
                    >
                        {logos.map((logo, index) => (
                            <div
                                key={logo.sizes.medium_large}
                                className={`
                                    tw:flex
                                    tw:items-stretch
                                    tw:gap-x-3
                                    tw:gap-y-9
                                    tw:basis-[calc(50%-3px)]
                                    tw:mob-l:basis-[auto]
                                    ${
                                        index > 5 && index <= 9
                                            ? 'tw:hidden tw:mob-l:block'
                                            : ''
                                    }
                                    ${index > 9 ? 'tw:hidden' : ''}
                                `}
                            >
                                <LogoImage
                                    src={logo.sizes.medium_large}
                                    alt={logo.alt}
                                    url={logo.url}
                                />
                            </div>
                        ))}
                    </div>
                </Module>
            )}
        </>
    );
}
